import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Icon,
  Select,
  StatePanel,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
  V2FormActions,
  V2FormField,
  V2FormGrid,
  V2Modal,
  V2Skeleton,
  V2ToastStack,
  type V2ToastItem,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPatch, apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JalaliDateTimeInput } from '../../lib/date/JalaliDateTimeInput.js';
import {
  dueTone,
  exactWhen,
  isoToWallClock,
  relativeWhen,
  wallClockToIso,
} from '../../lib/date/relativeWhen.js';
import {
  REMINDER_CHANNEL_OPTIONS,
  type Reminder,
  type ReminderListResponse,
} from '../../lib/types/projects.js';
import './project-reminders.css';

const SCHEDULED_LABEL = 'زمان‌بندی‌شده';
const CANCELLED_LABEL = 'لغو شده';
const TOAST_MS = 5000;

const fa = (n: number) => n.toLocaleString('fa-IR');

interface FormState {
  readonly editing: Reminder | null;
  readonly title: string;
  readonly remindAt: string;
  readonly channelLabel: string;
}

const EMPTY_FORM: FormState = { editing: null, title: '', remindAt: '', channelLabel: 'in_app' };

function channelValueOf(label: string): string {
  return (
    REMINDER_CHANNEL_OPTIONS.find((o) => o.label === label || o.value === label)?.value ?? 'in_app'
  );
}

/**
 * UI_REFERENCE_SOURCE: no dedicated Design Board — follows the design
 * system's own primitives directly, per the route inventory's PO decision.
 * canonical pattern: list (scheduled first, history below) + create/edit dialog
 * with cancel/retry actions. Edit is only offered for scheduled reminders
 * (the API rejects any other status).
 */
export function RemindersPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';

  const res = useApiGet<ReminderListResponse>(
    slug ? `${base}/api/v1/projects/reminders?limit=200` : null,
    [slug],
  );
  const fetched = res.status === 'ready' ? res.data.reminders : null;
  const [reminders, setReminders] = useState<readonly Reminder[] | null>(null);
  useEffect(() => {
    if (fetched) setReminders(fetched);
  }, [fetched]);

  const [form, setForm] = useState<FormState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ title?: string; remindAt?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const idempotencyKey = useRef('');
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const [toasts, setToasts] = useState<readonly V2ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: string) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  const toast = useCallback(
    (message: string, tone: NonNullable<V2ToastItem['tone']>) => {
      seq.current += 1;
      const id = `t${seq.current}`;
      setToasts((p) => [...p, { id, message, tone }]);
      window.setTimeout(() => dismiss(id), TOAST_MS);
    },
    [dismiss],
  );

  const { scheduled, history } = useMemo(() => {
    const all = reminders ?? [];
    return {
      scheduled: all
        .filter((r) => r.statusLabel === SCHEDULED_LABEL)
        .sort((a, b) => a.remindAt.localeCompare(b.remindAt)),
      history: all
        .filter((r) => r.statusLabel !== SCHEDULED_LABEL)
        .sort((a, b) => b.remindAt.localeCompare(a.remindAt)),
    };
  }, [reminders]);

  if (!slug) return null;

  function clearFieldError(key: 'title' | 'remindAt') {
    setFieldErrors((f) => {
      const next = { ...f };
      delete next[key];
      return next;
    });
  }

  function openCreate() {
    idempotencyKey.current = crypto.randomUUID();
    setFieldErrors({});
    setFormError(null);
    setForm(EMPTY_FORM);
  }

  function openEdit(r: Reminder) {
    setFieldErrors({});
    setFormError(null);
    setForm({
      editing: r,
      title: r.title,
      remindAt: isoToWallClock(r.remindAt),
      channelLabel: channelValueOf(r.channelLabel),
    });
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    setFormError(null);
    const title = form.title.trim();
    const errors: { title?: string; remindAt?: string } = {};
    if (!title) errors.title = 'عنوان یادآوری را وارد کنید.';
    const iso = form.remindAt ? wallClockToIso(form.remindAt) : null;
    if (!form.remindAt || iso === null) errors.remindAt = 'زمان یادآوری را انتخاب کنید.';
    else if (dueTone(iso) === 'overdue') errors.remindAt = 'زمان یادآوری باید در آینده باشد.';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      const payload = { title, remindAt: iso, channelLabel: form.channelLabel };
      if (form.editing) {
        await apiPatch(
          `${base}/api/v1/projects/reminders/${encodeURIComponent(form.editing.ref)}`,
          payload,
        );
      } else {
        await apiPost(`${base}/api/v1/projects/reminders`, payload, {
          headers: { 'idempotency-key': idempotencyKey.current },
        });
      }
      toast(form.editing ? 'یادآوری ویرایش شد.' : 'یادآوری ثبت شد.', 'success');
      setForm(null);
      res.reload();
    } catch (err) {
      setFormError(bannerMessageFrom(err, 'ثبت یادآوری ناموفق بود.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onAction(r: Reminder, action: 'cancel' | 'retry') {
    if (pending.has(r.ref)) return;
    setPending((p) => new Set(p).add(r.ref));
    try {
      await apiPost(`${base}/api/v1/projects/reminders/${encodeURIComponent(r.ref)}/${action}`);
      toast(action === 'cancel' ? 'یادآوری لغو شد.' : 'یادآوری دوباره زمان‌بندی شد.', 'success');
      res.reload();
    } catch {
      toast('عملیات انجام نشد؛ دوباره تلاش کنید.', 'danger');
    } finally {
      setPending((p) => {
        const next = new Set(p);
        next.delete(r.ref);
        return next;
      });
    }
  }

  function renderRow(r: Reminder) {
    const isScheduled = r.statusLabel === SCHEDULED_LABEL;
    const past = isScheduled && dueTone(r.remindAt) === 'overdue';
    const busy = pending.has(r.ref);
    return (
      <div
        key={r.ref}
        className={`prj-rem__row${past ? ' is-past' : ''}${busy ? ' is-pending' : ''}`}
      >
        <div className="prj-rem__main">
          <span className="prj-rem__title">{r.title}</span>
          <span className="prj-rem__when" title={exactWhen(r.remindAt)}>
            <Icon name="clock" size="sm" />
            {relativeWhen(r.remindAt)} · {r.channelLabel}
          </span>
        </div>
        <div className="prj-rem__actions">
          {past ? <V2Badge tone="danger">زمان گذشته</V2Badge> : null}
          {!isScheduled ? (
            <V2Badge tone={r.statusLabel === CANCELLED_LABEL ? 'neutral' : 'info'} plain>
              {r.statusLabel}
            </V2Badge>
          ) : null}
          {isScheduled ? (
            <>
              <V2Button size="sm" variant="ghost" disabled={busy} onClick={() => openEdit(r)}>
                ویرایش
              </V2Button>
              <V2Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => void onAction(r, 'cancel')}
              >
                لغو
              </V2Button>
            </>
          ) : null}
          {r.statusLabel === CANCELLED_LABEL ? (
            <V2Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => void onAction(r, 'retry')}
            >
              تلاش مجدد
            </V2Button>
          ) : null}
        </div>
      </div>
    );
  }

  let body;
  if (reminders === null && res.status === 'error') {
    body = (
      <StatePanel
        variant={res.error.status === 403 ? 'permission_denied' : 'network_error'}
        title={res.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
      />
    );
  } else if (reminders === null) {
    body = (
      <div aria-busy="true" aria-live="polite">
        {[0, 1, 2].map((i) => (
          <div key={i} className="prj-rem__skeleton-row">
            <V2Skeleton height={12} />
            <V2Skeleton width={72} height={22} />
          </div>
        ))}
      </div>
    );
  } else if (reminders.length === 0) {
    body = (
      <V2EmptyState
        icon="bell"
        title="هنوز یادآوری ثبت نشده است."
        description="برای تماس، پیگیری یا سررسید مهم یک یادآوری بسازید تا به‌موقع اعلان بگیرید."
        action={
          <V2Button variant="primary" icon="plus" onClick={openCreate}>
            یادآوری جدید
          </V2Button>
        }
      />
    );
  } else {
    body = (
      <>
        {scheduled.length === 0 ? (
          <V2EmptyState compact icon="check" title="یادآوری زمان‌بندی‌شده‌ای ندارید." />
        ) : (
          scheduled.map(renderRow)
        )}
        {history.length > 0 ? (
          <>
            <div className="prj-rem__section-label">سابقه ({fa(history.length)})</div>
            {history.map(renderRow)}
          </>
        ) : null}
      </>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card clipped>
        <V2CardHeader
          icon="bell"
          title="یادآورها"
          subtitle={reminders ? `${fa(scheduled.length)} مورد زمان‌بندی‌شده` : undefined}
          actions={
            <V2Button variant="primary" icon="plus" onClick={openCreate}>
              یادآوری جدید
            </V2Button>
          }
        />
        {body}
      </V2Card>

      <V2Modal
        open={form !== null}
        title={form?.editing ? 'ویرایش یادآوری' : 'یادآوری جدید'}
        onClose={() => setForm(null)}
      >
        {form ? (
          <form noValidate onSubmit={onSubmit}>
            <V2FormGrid>
              <V2FormField label="عنوان" required width="full" error={fieldErrors.title}>
                <input
                  className="sk-input"
                  value={form.title}
                  onChange={(e) => {
                    setForm({ ...form, title: e.target.value });
                    clearFieldError('title');
                  }}
                  aria-invalid={Boolean(fieldErrors.title) || undefined}
                />
              </V2FormField>
              <V2FormField label="زمان" required width="wide" error={fieldErrors.remindAt}>
                <JalaliDateTimeInput
                  value={form.remindAt}
                  onChange={(v) => {
                    setForm({ ...form, remindAt: v });
                    clearFieldError('remindAt');
                  }}
                />
              </V2FormField>
              <V2FormField label="کانال" width="medium">
                <Select
                  value={form.channelLabel}
                  onChange={(e) => setForm({ ...form, channelLabel: e.target.value })}
                >
                  {REMINDER_CHANNEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </V2FormField>
            </V2FormGrid>
            {formError ? (
              <Alert tone="danger" title="خطا">
                {formError}
              </Alert>
            ) : null}
            <div className="prj-rem__form-actions">
              <V2FormActions>
                <V2Button type="button" onClick={() => setForm(null)}>
                  انصراف
                </V2Button>
                <V2Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? 'در حال ثبت…' : form.editing ? 'ذخیره' : 'ثبت یادآوری'}
                </V2Button>
              </V2FormActions>
            </div>
          </form>
        ) : null}
      </V2Modal>

      <V2ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
